import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, MailCheck } from 'lucide-react';

import CarteAuth, { ACCENT, BoutonPrincipal, Champ, Message } from '../components/CarteAuth';
import ChampCode from '../components/ChampCode';
import { adminApi } from '../services/api.jsx';

/* ═══════════════════════════════════════════════════════════════════════════
   MOT DE PASSE OUBLIÉ

   Deux écrans. On demande l'identifiant ou l'adresse, puis on saisit le code
   à six chiffres reçu par e-mail. La vérification part toute seule dès la
   sixième case : aucun bouton de validation.

   La réponse du serveur est identique que l'adresse existe ou non — on passe
   donc à l'écran du code dans tous les cas. C'est délibéré : afficher
   « adresse inconnue » transformerait ce formulaire en annuaire, où l'on
   testerait des adresses jusqu'à savoir lesquelles ont un compte.
   ═══════════════════════════════════════════════════════════════════════════ */

export default function MotDePasseOublie() {
  const navigate = useNavigate();

  const [etape, setEtape] = useState('identifiant');
  const [identifiant, setIdentifiant] = useState('');
  const [code, setCode] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [verification, setVerification] = useState(false);
  const [erreur, setErreur] = useState('');
  const [attente, setAttente] = useState(0);

  const compteARebours = useRef(null);

  const lancerLeDecompte = useCallback((secondes) => {
    setAttente(secondes);
    clearInterval(compteARebours.current);
    compteARebours.current = setInterval(() => {
      setAttente((reste) => {
        if (reste <= 1) { clearInterval(compteARebours.current); return 0; }
        return reste - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(compteARebours.current), []);

  const demander = async (evenement) => {
    evenement?.preventDefault();
    if (!identifiant.trim()) return;
    setEnvoi(true);
    setErreur('');
    try {
      const reponse = await adminApi.demanderCode(identifiant.trim());
      setEtape('code');
      lancerLeDecompte(reponse.attente_secondes || 30);
    } catch (e) {
      setErreur(e.message || "La demande n'a pas abouti.");
    } finally {
      setEnvoi(false);
    }
  };

  // Déclenchée par la sixième case, jamais par un clic.
  const verifier = async (codeComplet) => {
    setVerification(true);
    setErreur('');
    try {
      const { jeton } = await adminApi.verifierCode(identifiant.trim(), codeComplet);
      // Le code ne change pas le mot de passe : il ouvre le même formulaire
      // que la première connexion. Un seul parcours à maintenir, un seul à
      // éprouver.
      navigate(`/activation?jeton=${encodeURIComponent(jeton)}`, { replace: true });
    } catch (e) {
      setErreur(e.message || "Code incorrect.");
      setCode('');
    } finally {
      setVerification(false);
    }
  };

  if (etape === 'identifiant') {
    return (
      <CarteAuth
        surtitre="Mot de passe oublié"
        titre="Retrouvons votre accès"
        description="Indiquez votre identifiant ou votre adresse e-mail. Un code à six chiffres vous sera envoyé."
        pied={<Link to="/login" className="inline-flex items-center gap-1.5 font-medium"
                    style={{ color: ACCENT }}>
                <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
                Revenir à la connexion
              </Link>}
      >
        <form onSubmit={demander} className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-[var(--text-main)]">
              Identifiant ou adresse e-mail
            </label>
            <Champ icone={Mail} type="text" required autoFocus
                   autoComplete="username"
                   placeholder="vous@organisation.org"
                   value={identifiant}
                   onChange={(e) => setIdentifiant(e.target.value)} />
          </div>

          <Message>{erreur}</Message>

          <BoutonPrincipal type="submit" disabled={envoi || !identifiant.trim()} aria-busy={envoi || undefined}>
            {envoi ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Envoi…</> : 'Recevoir un code'}
          </BoutonPrincipal>
        </form>
      </CarteAuth>
    );
  }

  return (
    <CarteAuth
      surtitre="Vérification"
      titre="Saisissez votre code"
      description={`Si un compte correspond à « ${identifiant} », un code à six chiffres vient d'y être envoyé.`}
      pied={<button onClick={() => { setEtape('identifiant'); setCode(''); setErreur(''); }}
                    className="inline-flex items-center gap-1.5 font-medium"
                    style={{ color: ACCENT }}>
              <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
              Changer d'adresse
            </button>}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-center gap-2 text-[13px] text-[var(--text-muted)]">
          <MailCheck className="w-4 h-4" style={{ color: ACCENT }} aria-hidden="true" />
          Code valable 10 minutes
        </div>

        <ChampCode
          valeur={code}
          onChange={setCode}
          onComplet={verifier}
          desactive={verification}
          erreur={Boolean(erreur)}
        />

        {verification && (
          <div className="flex items-center justify-center gap-2 text-[13px] text-[var(--text-muted)]"
               role="status" aria-live="polite">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Vérification…
          </div>
        )}

        <Message>{erreur}</Message>

        <div className="text-center text-[13px]">
          {attente > 0 ? (
            <span className="text-[var(--text-muted)]">
              Nouveau code possible dans {attente}&nbsp;s
            </span>
          ) : (
            <button type="button" onClick={() => demander()} disabled={envoi} aria-busy={envoi || undefined}
                    className="font-medium disabled:opacity-50" style={{ color: ACCENT }}>
              {envoi ? 'Envoi…' : "Je n'ai rien reçu — renvoyer un code"}
            </button>
          )}
        </div>

        <p className="text-center text-[12px] text-[var(--text-muted)] leading-relaxed">
          Pensez à regarder dans les courriers indésirables.
          <br />
          Le code expire après 10 minutes et 5 tentatives.
        </p>
      </div>
    </CarteAuth>
  );
}
